import dayjs from 'dayjs';
import isValidEmailAddress from '#base/helpers/validators/isValidEmailAddress';
import isValidPhoneNumber from '#base/helpers/validators/isValidPhoneNumber';
import type { AvailableDayType, AvailableWeekDaysType } from '#fysio/core/types/appointment-planner/AvailableDayType';
import type { NullablePatientDataType, PatientDataType } from '#fysio/core/types/appointment-planner/PatientDataType';
import type { SubmitResultType } from '#fysio/core/types/appointment-planner/SubmitResultType';
import type { TherapistType } from '#fysio/core/types/appointment-planner/TherapistType';
import type { TimeBlockType } from '#fysio/core/types/appointment-planner/TimeBlockType';

export const useAppointmentStore = defineStore('appointment', {
	state: (): {
		hasPlannedAppointment: boolean;
		isIntake: boolean;
		therapists: TherapistType[];
		availableDays: AvailableWeekDaysType[];
		selectedTherapist: TherapistType | null;
		timeBlockTherapist: TherapistType | null;
		selectedDay: AvailableDayType | null;
		selectedTimeBlock: TimeBlockType | null;
		patientData: PatientDataType | NullablePatientDataType;
		patientDataConfirmed: boolean;
		additionalInfo: string | null;
		submitResult: SubmitResultType | null;
		submittedAppointmentDate: Date | string | null;
	} => ({
		hasPlannedAppointment: false,
		isIntake: false,
		therapists: [],
		availableDays: [],
		selectedTherapist: null,
		timeBlockTherapist: null,
		selectedDay: null,
		selectedTimeBlock: null,
		patientData: {
			email: undefined,
			first_name: undefined,
			last_name: undefined,
			last_name_prefix: undefined,
			phone_number: undefined,
			newsletter: false,
		},
		patientDataConfirmed: false,
		additionalInfo: null,
		submitResult: null,
		submittedAppointmentDate: null, // This date persists to show, or not show the remaining slots banners on the website.
	}),

	actions: {
		setTherapists(therapists: TherapistType[]) {
			this.therapists = therapists;

			if (therapists.length === 1) {
				this.selectedTherapist = therapists[0] as TherapistType;
			}
		},
		setIsIntake(intake: boolean) {
			this.isIntake = intake;
		},
		setAvailableDays(availableDays: AvailableWeekDaysType[]) {
			this.availableDays = availableDays;
		},
		setSelectedTherapist(therapist: TherapistType) {
			this.selectedTherapist = therapist;
		},
		setTimeBlockTherapist(therapist: TherapistType) {
			this.timeBlockTherapist = therapist;
		},
		setSelectedDay(day: AvailableDayType) {
			this.selectedDay = day;
		},
		setSelectedTimeBlock(timeBlock: TimeBlockType) {
			this.selectedTimeBlock = timeBlock;
		},
		setPatientData(patientData: NullablePatientDataType) {
			this.patientData = patientData;
		},
		setPatientDataConfirmed(confirmed: boolean) {
			this.patientDataConfirmed = confirmed;
		},
		setAdditionalInfo(info: string) {
			this.additionalInfo = info;
		},
		setSubmitResult(result: SubmitResultType) {
			this.submitResult = result;
		},
		setHasPlannedAppointment(hasPlannedAppointment: boolean) {
			this.hasPlannedAppointment = hasPlannedAppointment;
		},
		setSubmittedAppointmentDate(date: Date | string) {
			this.submittedAppointmentDate = date;
		},
		resetStoreData() {
			this.selectedTherapist = null;
			this.selectedDay = null;
			this.selectedTimeBlock = null;
			this.additionalInfo = null;
			this.submitResult = null;
			this.hasPlannedAppointment = false;
		},
	},

	getters: {
		hasMultipleTherapists(state) {
			return state.therapists.length > 1;
		},
		hasAvailableWeeks(state) {
			return state.availableDays.filter(week => week.isAvailable).length > 0;
		},
		isPatientDataComplete(state) {
			switch (undefined) {
				case state.patientData.email:
				case state.patientData.first_name:
				case state.patientData.last_name:
				case state.patientData.phone_number:
					return false;
				default:
					return true;
			}
		},
		isPatientDataValid(state) {
			const emailValid = isValidEmailAddress(state.patientData.email ?? '');
			const phoneValid = isValidPhoneNumber(state.patientData.phone_number ?? '');

			return emailValid && phoneValid;
		},
		isAppointmentDataComplete(state) {
			return state.selectedTherapist
				&& state.selectedDay
				&& state.selectedTimeBlock;
		},
		appointmentDataOverview(state) {
			return {
				therapist: {
					title: 'Therapeut',
					value: state.timeBlockTherapist?.full_name,
				},
				day: {
					title: 'Dag',
					value: state.selectedDay?.day + ' - ' + dayjs(state.selectedDay?.date).format('DD-MM-YYYY'),
				},
				timeBlock: {
					title: 'Tijd',
					value: dayjs(state.selectedTimeBlock?.from).format('HH.mmu'),
				},
				additionalInfo: {
					title: 'Opgegeven informatie',
					value: state.additionalInfo ?? '-',
				},
			};
		},
		patientDataOverview(state) {
			return {
				first_name: {
					title: 'Voornaam',
					value: state.patientData.first_name,
				},
				full_last_name: {
					title: 'Achternaam',
					value: state.patientData.last_name_prefix
						? state.patientData.last_name_prefix + ' ' + state.patientData.last_name
						: state.patientData.last_name,
				},
				email: {
					title: 'E-mailadres',
					value: state.patientData.email,
				},
				phone_number: {
					title: 'Telefoonnummer',
					value: state.patientData.phone_number,
				},
			};
		},
	},

	persist: {
		storage: piniaPluginPersistedstate.cookies({
			maxAge: 14 * 24 * 60 * 60,
		}),
		pick: ['patientData', 'submittedAppointmentDate'],
	},
});
